import {
	makeComputationApiRepository,
	makeMonitorRepository,
	makeReferentialApiRepository,
} from "../../core/repositories/repositoryBuilders.js";
import AccountingDocumentService from "../shared/documentInfrastructure/AccountingDocumentService.js";
import Budget from "../../models/Budget.js";
import ChartOfAccounts from "../../models/chartOfAccounts/ChartOfAccounts.js";
import Forecast from "../../models/forecast/instance/Forecast.js";
import ForecastComputationResult from "../../models/forecast/instance/ForecastComputationResult.js";
import ForecastTemplateInterpretationContext from "../../models/forecast/interpretation/ForecastTemplateInterpretationContext.js";
import ForecastTemplateService from "../../core/services/ForecastTemplateService.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import InternationalizationService from "../../core/services/InternationalizationService.js";
import ListSource from "../../core/list/ListSource.js";
import ODataStream from "../../core/list/streams/ODataStream.js";
import RuleTemplate from "../../models/forecast/template/RuleTemplate.js";
import VatSettings from "../../models/VatSettings.js";

/**
 * Service responsible for all data exchange required by the forecast
 * editor document.  The receiver coordinates other services and repositories.
 *
 * @param {session} session - The user's session. Used to get the
 *   current company, current period…
 * @param {ForecastTemplateService} [forecastTemplateService] - The
 *   service handling CRUD operations on `ForecastTemplate` instances
 * @param {Period} period - The most recent period used for actuals.
 *   Periods after that will be predicted
 */
export default class ForecastEditorDocumentService extends AccountingDocumentService {
	constructor({
		session,
		period,
		forecastTemplateService = new ForecastTemplateService({
			company: session.getCompany(),
			countryId: session.getCountryId(),
		}),
	} = {}) {
		super(...arguments);

		this._session = session;
		this._period = period;
		this._forecastTemplateService = forecastTemplateService;

		this._chartOfAccountsRepository = makeComputationApiRepository({
			route: "ForecastTemplates/#forecastTemplateId/ChartOfAccounts",
			model: ChartOfAccounts,
			session,
		});

		this._keyFigureService = getKeyFigureService();

		this._budgetRepository = makeMonitorRepository({
			route: "Accounting2/Company/#companyId/Budget/?id",
			model: Budget,
			defaultFilters: { companyId: session.getCompanyId() },
			antiForgeryToken: session.getAntiForgeryToken(),
		});

		this._computationRepository = makeComputationApiRepository({
			route: "ForecastTemplates/#id/Series",
			model: ForecastComputationResult,
			session,
		});

		this._ruleTemplateRepository = makeComputationApiRepository({
			route: "ForecastTemplates/#forecastTemplateId/RuleTemplates/?id",
			model: RuleTemplate,
			session,
		});

		this._scenarioRuleTemplateRepository = makeComputationApiRepository({
			route: "ForecastTemplates/#forecastTemplateId/Scenarios/#scenarioId/RuleTemplates",
			model: RuleTemplate,
			session,
		});

		this._forecastRepository = makeComputationApiRepository({
			route: "/Forecasts",
			session,
			model: Forecast,
		});

		this._vatSettingsRepository = makeReferentialApiRepository({
			route: "Settings/VAT",
			session,
			model: VatSettings,
		});
	}

	/**
	 * Request the initial data required by the document.
	 */
	async fetchInitialData({ forecastTemplateId }) {
		await super.fetchInitialData(...arguments);

		const vatSettings = await this._vatSettingsRepository.get();

		this._scenarioRuleTemplateRepository.addToContext({
			vatRates: vatSettings.getVatRates(),
		});

		this._ruleTemplateRepository.addToContext({
			vatRates: vatSettings.getVatRates(),
		});

		const [{ forecastTemplate, chartOfAccounts, keyFigures }, budgets] =
			await Promise.all([
				this._fetchForecastTemplateAndItsDependencies({
					forecastTemplateId,
				}),
				this._budgetRepository.get(),
			]);

		this._computationRepository.addToContext({ keyFigures });

		// Since the FrozenCurrentPeriod can be null in the backend, we're using
		// `||` instead of a default value.
		const period =
			forecastTemplate.getFrozenCurrentPeriod() || this._period;

		const interpretationContext = this._makeInterpretationContext({
			chartOfAccounts,
			keyFigures,
			period,
		});

		return {
			forecastTemplate,
			interpretationContext,
			budgets,
			vatSettings,
		};
	}

	computeForecastSeriesSpecifications({
		computationRequest,
		forecastTemplateId,
	}) {
		return this._computationRepository.save({
			model: computationRequest,
			filter: { id: forecastTemplateId },
			notify: false,
		});
	}

	getRuleTemplateSource({ forecastTemplateId }) {
		return ListSource.makeAndFetch({
			stream: new ODataStream({
				fetcher: ({ filter }) =>
					this._ruleTemplateRepository.get({
						filter: { ...filter, forecastTemplateId },
					}),
			}),
		});
	}

	createRuleTemplate({ ruleTemplate, forecastTemplateId, notify }) {
		return this._ruleTemplateRepository.save({
			model: ruleTemplate,
			filter: { forecastTemplateId },
			notify,
		});
	}

	updateRuleTemplate({ ruleTemplate, forecastTemplateId, notify }) {
		return this._ruleTemplateRepository.update({
			model: ruleTemplate,
			filter: { forecastTemplateId, id: ruleTemplate.getId() },
			readResponse: false,
			notify,
		});
	}

	removeRuleTemplate({ ruleTemplateId, forecastTemplateId, notify }) {
		return this._ruleTemplateRepository.remove({
			filter: { forecastTemplateId, id: ruleTemplateId },
			readResponse: false,
			notify,
		});
	}

	publishForecast({ computationRequest, name, forecast }) {
		return this._forecastRepository.save({
			filter: {
				forecastTemplateId: forecast.getForecastTemplateId(),
				name,
			},
			model: computationRequest,
			notify: false,
		});
	}

	updateForecastTemplateConfiguration({
		forecastTemplateConfiguration,
		forecastTemplateId,
		notify,
	}) {
		return this._forecastTemplateService.updateForecastTemplateConfiguration(
			forecastTemplateConfiguration,
			forecastTemplateId,
			notify,
		);
	}

	_makeInterpretationContext({ chartOfAccounts, keyFigures, period }) {
		return new ForecastTemplateInterpretationContext({
			chartOfAccounts,
			keyFigures,
			period,
			company: this._session.getCompany(),
		});
	}

	async _fetchForecastTemplateAndItsDependencies({ forecastTemplateId }) {
		const forecastTemplate =
			await this._forecastTemplateService.get(forecastTemplateId);

		const [chartOfAccounts, keyFigures, ruleTemplates] = await Promise.all([
			this._chartOfAccountsRepository.get({
				filter: { forecastTemplateId },
			}),
			this._keyFigureService.getAllFromCache(),
			this._scenarioRuleTemplateRepository.get({
				filter: {
					forecastTemplateId: forecastTemplate.getId(),
					scenarioId: "Baseline",
				},
			}),
		]);

		forecastTemplate.getBaselineScenario().setRuleTemplates(ruleTemplates);
		await this._ensureChartOfAccountsLanguage(chartOfAccounts);

		return { forecastTemplate, chartOfAccounts, keyFigures };
	}

	_ensureChartOfAccountsLanguage(chartOfAccounts) {
		const chartOfAccountsLanguage = chartOfAccounts.getLanguage();

		if (!chartOfAccountsLanguage) {
			return Promise.resolve();
		}

		return InternationalizationService.instance().ensureTranslations(
			chartOfAccountsLanguage,
		);
	}
}
